import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AppErrorBoundary } from "./components/layout/AppErrorBoundary";
import { PublicShell } from "./components/layout/PublicShell";
import { PortalLayout } from "./components/layout/PortalLayout";
import { RequireAuth } from "./components/layout/RequireAuth";
import { AdminClientsPage } from "./pages/admin/AdminClientsPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminMemoryMediaVaultPage } from "./pages/admin/AdminMemoryMediaVaultPage";
import {
  AdminAgreementRecordsPage,
  AdminPricingCatalogPage,
  AdminProposalDashboardPage,
  AdminProposalsPage,
  AdminServiceAgreementsPage,
} from "./pages/admin/AdminProposalSystemPages";
import { AdminSchedulerPage } from "./pages/admin/AdminSchedulerPage";
import { AdminUserManualPage } from "./pages/admin/AdminUserManualPage";
import {
  AdminActivityLogPage,
  AdminClientDetailPage,
  AdminEventsPage,
  AdminMediaPage,
  AdminSettingsPage,
  AdminStorageSetupPage,
} from "./pages/admin/AdminUtilityPages";
import { DashboardPage } from "./pages/client/DashboardPage";
import { InstructionsPage } from "./pages/client/InstructionsPage";
import { MediaManagementPage } from "./pages/client/MediaManagementPage";
import { MemoryMediaVaultPage } from "./pages/client/MemoryMediaVaultPage";
import { SettingsPage } from "./pages/client/SettingsPage";
import { StorageBillingPage } from "./pages/client/StorageBillingPage";
import { UploadLinkPage } from "./pages/client/UploadLinkPage";
import { GalleryPage } from "./pages/GalleryPage";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { AgreementReviewPage, ProposalReviewPage } from "./pages/forms/ProposalReviewPage";
import { MemoryHubPage } from "./pages/MemoryHubPage";
import { UploadPage } from "./pages/UploadPage";

export function App() {
  return (
    <AppErrorBoundary>
      <Routes>
        <Route element={<PublicShell />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/upload-link/:uploadSlug" element={<UploadPage />} />
          <Route path="/upload/:uploadSlug" element={<UploadPage />} />
          <Route path="/upload/:clientSlug/:eventSlug" element={<UploadPage />} />
          <Route path="/gallery/:clientSlug/:eventSlug" element={<GalleryPage />} />
          <Route path="/memory-media-vault/:clientSlug/:eventSlug" element={<MemoryHubPage />} />
          <Route path="/memory-hub/:clientSlug/:eventSlug" element={<LegacyMemoryVaultRedirect />} />
          <Route path="/proposal/:token" element={<ProposalReviewPage />} />
          <Route path="/agreement/:token" element={<AgreementReviewPage />} />
        </Route>

        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin/login" element={<LoginPage admin />} />

        <Route element={<RequireAuth />}>
          <Route element={<PortalLayout />}>
            <Route path="/client/dashboard" element={<DashboardPage />} />
            <Route path="/client/memory-media-vault" element={<MemoryMediaVaultPage />} />
            <Route path="/client/memory-hub/vault" element={<Navigate to="/client/memory-media-vault" replace />} />
            <Route path="/client/pending" element={<MediaManagementPage view="pending" />} />
            <Route path="/client/approved" element={<MediaManagementPage view="approved" />} />
            <Route path="/client/featured" element={<MediaManagementPage view="featured" />} />
            <Route path="/client/hidden" element={<MediaManagementPage view="hidden" />} />
            <Route path="/client/not-approved" element={<MediaManagementPage view="not-approved" />} />
            <Route path="/client/upload-link" element={<UploadLinkPage />} />
            <Route path="/client/guest-upload-link" element={<Navigate to="/client/upload-link" replace />} />
            <Route path="/client/storage-billing" element={<StorageBillingPage />} />
            <Route path="/client/settings" element={<SettingsPage />} />
            <Route path="/client/instructions" element={<InstructionsPage />} />
          </Route>
        </Route>

        <Route element={<RequireAuth admin />}>
          <Route element={<PortalLayout mode="admin" />}>
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/proposal-dashboard" element={<AdminProposalDashboardPage />} />
            <Route path="/admin/proposals" element={<AdminProposalsPage />} />
            <Route path="/admin/service-agreements" element={<AdminServiceAgreementsPage />} />
            <Route path="/admin/agreement-records" element={<AdminAgreementRecordsPage />} />
            <Route path="/admin/pricing-catalog" element={<AdminPricingCatalogPage />} />
            <Route path="/admin/scheduling" element={<AdminSchedulerPage />} />
            <Route path="/admin/clients" element={<AdminClientsPage />} />
            <Route path="/admin/memory-media-vault" element={<AdminMemoryMediaVaultPage />} />
            <Route path="/admin/memory-hub/vault" element={<Navigate to="/admin/memory-media-vault" replace />} />
            <Route path="/admin/client/:clientId" element={<AdminClientDetailPage />} />
            <Route path="/admin/events" element={<AdminEventsPage />} />
            <Route path="/admin/media" element={<AdminMediaPage />} />
            <Route path="/admin/storage-setup" element={<AdminStorageSetupPage />} />
            <Route path="/admin/activity-log" element={<AdminActivityLogPage />} />
            <Route path="/admin/user-manual" element={<AdminUserManualPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<HomePage />} />
      </Routes>
    </AppErrorBoundary>
  );
}

function LegacyMemoryVaultRedirect() {
  const { clientSlug = "", eventSlug = "" } = useParams();
  return <Navigate to={`/memory-media-vault/${clientSlug}/${eventSlug}`} replace />;
}
